import React, {useEffect, useMemo, useState} from 'react'
import {
	Box,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	InputAdornment,
	TextField,
	Tooltip,
	Typography,
}                                            from '@mui/material'
import {CloseRounded, SearchRounded}         from '@mui/icons-material'
import {
	CATEGORY_ICON_GROUPS,
	CATEGORY_ICON_NAMES,
	humanizeIconName,
}                                            from '../../util/category-icons'
import {suggestCategoryIcons}                from '../../util/admin'
import CategoryIcon                          from '../CategoryIcon'

/*
 * The icon picker (102).
 *
 * Every icon the app can render, grouped, with a filter box — and the model's picks for this
 * category pinned above them so the common case is one click.
 *
 * The suggestions need a category name and nothing else: token and colours say nothing about the
 * subject. Until the modal has one, the recommended row says so instead of showing an empty strip.
 *
 * Suggestions are fetched once per open rather than on every keystroke of the filter — the filter
 * searches the registry, not the model.
 */
const IconPickerDialog = ({open, name, icon, color, color2, onSelect, onClose}) => {
	const [query, setQuery] = useState('')
	const [recommended, setRecommended] = useState([])
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState('')

	const hasName = Boolean(name && name.trim())

	useEffect(() => {
		if (!open) return

		setQuery('')
		setError('')
		setRecommended([])

		if (!hasName) return

		let cancelled = false
		setLoading(true)

		suggestCategoryIcons(name, CATEGORY_ICON_NAMES)
			.then(icons => { if (!cancelled) setRecommended(icons) })
			.catch(e => { if (!cancelled) setError(e.message || 'Could not get suggestions') })
			.finally(() => { if (!cancelled) setLoading(false) })

		return () => { cancelled = true }
	}, [open, name, hasName])

	// filter matches the humanised icon name and the group name both, so "legal" finds the gavel
	const groups = useMemo(() => {
		const q = query.trim().toLowerCase()
		return CATEGORY_ICON_GROUPS
			.map(g => {
				const groupMatches = g.group.toLowerCase().includes(q)
				const names = Object.keys(g.icons).filter(n =>
					!q || groupMatches || humanizeIconName(n).toLowerCase().includes(q)
				)
				return {group: g.group, names}
			})
			.filter(g => g.names.length > 0)
	}, [query])

	const matchCount = groups.reduce((n, g) => n + g.names.length, 0)

	const renderTile = (iconName) => (
		<Tooltip key={iconName} title={humanizeIconName(iconName)} placement="top">
			<Box
				onClick={() => onSelect(iconName)}
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					gap: 0.5,
					p: 1,
					cursor: 'pointer',
					borderRadius: 1.5,
					border: '1px solid',
					borderColor: iconName === icon ? 'primary.main' : 'transparent',
					'&:hover': {backgroundColor: 'action.hover'},
				}}
			>
				<CategoryIcon icon={iconName} color={color} color2={color2} size={40}/>
				<Typography
					variant="caption"
					sx={{
						width: 72,
						textAlign: 'center',
						lineHeight: 1.2,
						color: 'text.secondary',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						display: '-webkit-box',
						WebkitLineClamp: 2,
						WebkitBoxOrient: 'vertical',
					}}
				>
					{humanizeIconName(iconName)}
				</Typography>
			</Box>
		</Tooltip>
	)

	const grid = (names) => (
		<Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
			{names.map(renderTile)}
		</Box>
	)

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
				<span>Choose an icon</span>
				<IconButton onClick={onClose} size="small">
					<CloseRounded/>
				</IconButton>
			</DialogTitle>
			<DialogContent dividers>
				<TextField
					autoFocus
					fullWidth
					size="small"
					placeholder="Filter icons — try a subject, like &quot;home&quot;, &quot;legal&quot; or &quot;medical&quot;"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchRounded fontSize="small"/>
							</InputAdornment>
						),
					}}
				/>

				{/* AI picks for this category, above the library */}
				<Box mt={2}>
					<Typography variant="subtitle2" color="text.secondary" gutterBottom>
						{hasName ? `Recommended for "${name}"` : 'Recommended'}
					</Typography>
					{!hasName && (
						<Typography variant="body2" color="text.secondary">
							Enter a category name to get suggestions.
						</Typography>
					)}
					{hasName && loading && (
						<Box display="flex" alignItems="center" gap={1} py={1}>
							<CircularProgress size={18}/>
							<Typography variant="body2" color="text.secondary">
								Picking icons for this category…
							</Typography>
						</Box>
					)}
					{hasName && !loading && error && (
						<Typography variant="body2" color="error">{error}</Typography>
					)}
					{hasName && !loading && !error && recommended.length === 0 && (
						<Typography variant="body2" color="text.secondary">
							No suggestions came back — pick one from the library below.
						</Typography>
					)}
					{recommended.length > 0 && grid(recommended)}
				</Box>

				<Divider sx={{my: 2}}/>

				<Typography variant="subtitle2" color="text.secondary" gutterBottom>
					All icons ({matchCount})
				</Typography>
				{groups.map(g => (
					<Box key={g.group} mb={2}>
						<Typography variant="overline" color="text.secondary">
							{g.group}
						</Typography>
						{grid(g.names)}
					</Box>
				))}
				{matchCount === 0 && (
					<Typography variant="body2" color="text.secondary">
						No icons match "{query}".
					</Typography>
				)}
			</DialogContent>
		</Dialog>
	)
}

export default IconPickerDialog
